import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./LegalPolicies.scss";

const TABS = [
  { key: "terms", label: "Platform Terms of Use" },
  { key: "privacy", label: "Privacy Notice" },
  { key: "cookies", label: "Cookie Notice" },
  { key: "contact", label: "Contact" },
];

const LegalPolicies = () => {
  const [activeTab, setActiveTab] = useState("terms");
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-page-inner">
        <div className="legal-page-header">
          <div>
            <h1 className="legal-page-title">Legal Policies</h1>
            <p className="legal-page-subtitle">
              Platform terms, privacy, cookies and contact information for
              AscensionQ.
            </p>
          </div>
          <button
            type="button"
            className="legal-back-btn"
            onClick={() => navigate(-1)}
          >
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>

        <div className="legal-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`legal-tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="legal-page-body">
          {activeTab === "terms" && <TermsOfUseTab />}
          {activeTab === "privacy" && <PrivacyNoticeTab />}
          {activeTab === "cookies" && <CookieNoticeTab />}
          {activeTab === "contact" && <ContactTab />}
        </div>
      </div>
    </div>
  );
};

const TermsOfUseTab = () => (
  <>
    <div className="privacy-content-meta">
      <span className="privacy-content-meta-label">Version:</span>
      <span className="privacy-content-meta-date">1.0</span>
      <span className="privacy-content-meta-label">Effective date:</span>
      <span className="privacy-content-meta-date">1st May 2026</span>
    </div>

    <section className="privacy-section">
      <h2 className="privacy-section-title">1. Introduction</h2>
      <p>
        These Platform Terms of Use govern access to and use of the AscensionQ
        platform, including the eISF, eTMF and associated modules and services
        made available through it.
      </p>
      <p>
        By clicking &ldquo;Sign in&rdquo;, &ldquo;I Accept&rdquo;, or otherwise
        accessing or using the platform, you confirm that:
      </p>
      <ul>
        <li>you are an authorised user acting on behalf of your organisation;</li>
        <li>you have read and understood these Terms of Use;</li>
        <li>you agree to comply with them; and</li>
        <li>
          you will use the platform only for lawful and authorised business
          purposes.
        </li>
      </ul>
      <p>
        If you are not authorised to use the platform, you must not access or
        use it.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">2. About AscensionQ</h2>
      <p>
        AscensionQ is operated by Ascension-Q Ltd, a company registered in
        England and Wales under company number 10833152, with its registered
        office at C/O 264 Manchester Road, Warrington, Cheshire, WA1 3RB.
      </p>
      <ul>
        <li>
          General enquiries:{" "}
          <a href="mailto:info@aq-trials.com">info@aq-trials.com</a>
        </li>
        <li>
          Support enquiries:{" "}
          <a href="mailto:support@aq-trials.com">support@aq-trials.com</a>
        </li>
        <li>
          Legal enquiries:{" "}
          <a href="mailto:legal@aq-trials.com">legal@aq-trials.com</a>
        </li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">3. Scope</h2>
      <p>
        These Terms of Use apply to individual users accessing the AscensionQ
        hosted platform on behalf of their organisation.
      </p>
      <p>
        These Terms of Use do not replace any subscription agreement, services
        agreement, data processing agreement or other contract entered into
        between Ascension-Q Ltd and your organisation. If there is any
        inconsistency between these Terms of Use and the contract with your
        organisation, the organisation-level contract shall prevail to the extent
        of that inconsistency.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">4. Authorised Use Only</h2>
      <p>You may use the platform only if:</p>
      <ul>
        <li>your organisation has authorised your access;</li>
        <li>
          you are using the platform in the course of your professional duties;
          and
        </li>
        <li>
          your access rights have not been suspended, restricted or withdrawn.
        </li>
      </ul>
      <p>You must not:</p>
      <ul>
        <li>
          allow any other person to use your account or credentials;
        </li>
        <li>misrepresent your identity, role or authority; or</li>
        <li>
          attempt to access any part of the platform, data or functionality
          beyond your authorised permissions.
        </li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">5. Permitted Use</h2>
      <p>
        You may use the platform solely for legitimate and authorised purposes
        connected with clinical research, quality management, study
        documentation, oversight, compliance and related operational activities
        of your organisation.
      </p>
      <p>You must use the platform:</p>
      <ul>
        <li>
          in accordance with your organisation&rsquo;s policies, procedures and
          delegated authority;
        </li>
        <li>
          in accordance with applicable law and regulatory requirements relevant
          to your role and your organisation&rsquo;s use of the platform; and
        </li>
        <li>only within the permissions assigned to you.</li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">
        6. Security and Account Responsibilities
      </h2>
      <p>
        You are responsible for maintaining the confidentiality and security of
        your login credentials and any authentication factors issued to you.
      </p>
      <p>You must:</p>
      <ul>
        <li>
          keep usernames, passwords, access codes and authentication factors
          confidential;
        </li>
        <li>
          use multi-factor authentication and other security controls where
          required;
        </li>
        <li>
          notify your organisation or system administrator immediately if you
          suspect unauthorised access, account compromise or misuse; and
        </li>
        <li>
          use the platform only from appropriately secured devices and networks.
        </li>
      </ul>
      <p>
        You must not attempt to bypass, disable, test or interfere with any
        authentication, monitoring or security control.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">7. Acceptable Use Restrictions</h2>
      <p>You must not:</p>
      <ul>
        <li>
          use the platform for any unlawful, fraudulent, misleading or
          unauthorised purpose;
        </li>
        <li>
          upload, store, process or share information unless you are authorised
          to do so;
        </li>
        <li>
          upload participant-identifiable data or special category personal data
          unless your organisation is authorised and configured to use the
          platform for that purpose;
        </li>
        <li>
          introduce malware, ransomware, viruses or other malicious code;
        </li>
        <li>
          use scripts, bots, crawlers or automated tools to access, monitor or
          extract information from the platform without express written
          permission;
        </li>
        <li>
          reverse engineer, decompile, copy, modify or create derivative works
          from the platform except to the extent that applicable law does not
          permit this restriction;
        </li>
        <li>
          falsify, alter, delete, suppress or attempt to interfere with audit
          trail records, compliance records or system logs; or
        </li>
        <li>
          sign, approve or acknowledge documents on behalf of another person
          without proper authority.
        </li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">8. Confidentiality</h2>
      <p>
        Information made available through the platform may be confidential,
        commercially sensitive, legally privileged or subject to regulatory
        controls.
      </p>
      <p>You must:</p>
      <ul>
        <li>keep such information confidential;</li>
        <li>access only the information you are authorised to access;</li>
        <li>
          not disclose, export, copy or share information except where authorised
          and lawful; and
        </li>
        <li>
          comply with all confidentiality, information governance, data
          protection and record-handling obligations that apply to your role.
        </li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">9. Monitoring and Audit</h2>
      <p>
        Use of the platform may be monitored and recorded for security, support,
        compliance, audit, integrity and regulatory purposes. Monitoring may
        include login activity, access attempts, document actions, workflow
        actions, administrative actions and audit trail events.
      </p>
      <p>
        By using the platform, you acknowledge and accept such monitoring and
        logging.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">10. Privacy</h2>
      <p>
        AscensionQ processes certain personal data relating to user accounts,
        access management, support, system security and platform operations.
        Further information is set out in the Privacy Notice.
      </p>
      <p>
        Your organisation is responsible for determining what information may be
        uploaded to the platform and for ensuring that it has the necessary legal
        basis, permissions and authority for such use.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">11. Intellectual Property</h2>
      <p>
        All intellectual property rights in the platform, software, design,
        structure, documentation and related materials belong to Ascension-Q Ltd
        and its licensors, except for customer data and third-party materials
        lawfully provided by customers.
      </p>
      <p>
        You are granted a limited, revocable, non-exclusive, non-transferable
        right to access and use the platform during the period that your
        organisation authorises your access.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">
        12. Suspension and Withdrawal of Access
      </h2>
      <p>
        AscensionQ or your organisation may suspend, restrict or remove your
        access at any time where reasonably necessary, including where:
      </p>
      <ul>
        <li>your authorisation has ended;</li>
        <li>
          there is suspected misuse, unlawful activity or breach of these Terms
          of Use;
        </li>
        <li>there is a security incident or suspected compromise;</li>
        <li>
          suspension is needed to protect the platform, data or other users; or
        </li>
        <li>
          required by law, regulation, sponsor instruction, court order or
          organisational policy.
        </li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">13. No Professional Advice</h2>
      <p>
        The platform is provided as a software tool to support document
        management, compliance and operational processes. It does not constitute
        legal advice, regulatory advice, clinical advice or professional
        judgement.
      </p>
      <p>
        You and your organisation remain responsible for your own legal,
        regulatory, clinical and operational decisions.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">14. Changes to These Terms</h2>
      <p>
        AscensionQ may update these Terms of Use from time to time to reflect
        changes to the platform, law, regulation, security requirements or
        service delivery. Updated terms may be presented to users for review and
        acceptance on a future login.
      </p>
    </section>

    <section className="privacy-section privacy-section-last">
      <h2 className="privacy-section-title">15. Governing Law</h2>
      <p>
        These Terms of Use and any dispute or claim arising out of or in
        connection with them shall be governed by the laws of England and Wales,
        and the courts of England and Wales shall have exclusive jurisdiction,
        subject to any overriding contractual arrangements between AscensionQ
        and your organisation.
      </p>
    </section>
  </>
);

const PrivacyNoticeTab = () => (
  <>
    <div className="privacy-content-meta">
      <span className="privacy-content-meta-label">Version:</span>
      <span className="privacy-content-meta-date">1.0</span>
      <span className="privacy-content-meta-label">Effective date:</span>
      <span className="privacy-content-meta-date">1st May 2026</span>
    </div>

    <section className="privacy-section">
      <h2 className="privacy-section-title">1. Introduction</h2>
      <p>
        Ascension-Q Ltd is committed to protecting personal data and handling it
        in a lawful, fair and transparent manner.
      </p>
      <p>
        This Privacy Notice explains how AscensionQ collects, uses and protects
        personal data relating to users of the platform, including users
        accessing the system for login, authentication, support, security, audit
        and system administration purposes.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">2. Who We Are</h2>
      <p>
        Ascension-Q Ltd is a company registered in England and Wales under
        company number 10833152, with its registered office at C/O 264
        Manchester Road, Warrington, Cheshire, WA1 3RB.
      </p>
      <p>
        For privacy enquiries, please contact:{" "}
        <a href="mailto:privacy@aq-trials.com">privacy@aq-trials.com</a>
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">3. Personal Data We Collect</h2>
      <p>
        We may collect and use the following categories of personal data:
      </p>
      <ul>
        <li>
          identity and profile data, such as name, job title, role, department
          and organisation;
        </li>
        <li>
          contact data, such as work email address and business telephone number
          where provided;
        </li>
        <li>
          account and authentication data, such as username, password-related
          data, access codes, login history and authentication events;
        </li>
        <li>
          technical data, such as IP address, browser type, device information,
          operating system, timestamps and platform usage details;
        </li>
        <li>
          audit and activity data, such as document actions, access logs,
          workflow actions, approval history, comments, administrative actions
          and other audit trail events;
        </li>
        <li>
          support and communications data, such as messages, tickets,
          troubleshooting information and correspondence.
        </li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">4. How We Collect Personal Data</h2>
      <p>We collect personal data:</p>
      <ul>
        <li>
          directly from you when you sign in, use the platform, contact support
          or interact with the system;
        </li>
        <li>
          from your organisation or system administrator when your user account
          is created or managed;
        </li>
        <li>
          automatically through the platform and related systems when you access
          or use the service.
        </li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">5. How We Use Personal Data</h2>
      <p>We use personal data for the following purposes:</p>
      <ul>
        <li>to create, administer and manage user accounts;</li>
        <li>to authenticate users and maintain system security;</li>
        <li>to provide access to the platform and its features;</li>
        <li>to operate, support, maintain and improve the platform;</li>
        <li>
          to generate and preserve audit trails, logs and compliance records;
        </li>
        <li>to monitor system use, investigate incidents and prevent misuse;</li>
        <li>to respond to support enquiries and service issues;</li>
        <li>to comply with legal, regulatory and contractual obligations; and</li>
        <li>
          to protect the rights, property, systems and legitimate interests of
          AscensionQ, its customers and users.
        </li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">6. Lawful Bases for Processing</h2>
      <p>
        We process personal data where necessary for one or more of the
        following lawful bases:
      </p>
      <ul>
        <li>performance of a contract;</li>
        <li>compliance with a legal obligation;</li>
        <li>
          legitimate interests pursued by AscensionQ or its customers, including
          security, service administration, fraud prevention, audit, compliance
          and platform integrity; and
        </li>
        <li>where applicable, consent.</li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">
        7. Controller and Processor Roles
      </h2>
      <p>
        For user account, login, support, service administration, security and
        similar operational data, Ascension-Q Ltd may act as a controller.
      </p>
      <p>
        For personal data uploaded to or managed within the platform by customer
        organisations, Ascension-Q Ltd will generally act as a processor on
        behalf of the relevant customer organisation, subject to the applicable
        customer contract and data processing agreement.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">8. Sharing of Personal Data</h2>
      <p>We may share personal data where necessary with:</p>
      <ul>
        <li>your organisation and its authorised administrators;</li>
        <li>
          service providers and subprocessors supporting hosting,
          infrastructure, security, communications, support and related
          operational services;
        </li>
        <li>professional advisers, auditors and insurers where necessary;</li>
        <li>
          regulators, law enforcement bodies, courts or other authorities where
          required by law or where necessary to protect legal rights.
        </li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">9. International Transfers</h2>
      <p>
        Where personal data is transferred outside the UK, we will take steps to
        ensure that appropriate safeguards are in place as required by applicable
        data protection law.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">10. Data Retention</h2>
      <p>
        We retain personal data only for as long as necessary for the purposes
        for which it was collected, including for security, audit, compliance,
        legal, regulatory and contractual purposes.
      </p>
      <p>
        Retention periods may vary depending on the type of data, the nature of
        the customer relationship, legal obligations, regulatory requirements and
        the need to preserve system and audit integrity.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">11. Your Rights</h2>
      <p>
        Subject to applicable law, individuals may have rights in relation to
        their personal data, including the right to:
      </p>
      <ul>
        <li>request access to personal data;</li>
        <li>request correction of inaccurate or incomplete personal data;</li>
        <li>request erasure in certain circumstances;</li>
        <li>request restriction of processing in certain circumstances;</li>
        <li>object to processing in certain circumstances;</li>
        <li>request portability where applicable; and</li>
        <li>
          lodge a complaint with the Information Commissioner&rsquo;s Office.
        </li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">12. Security</h2>
      <p>
        AscensionQ uses technical and organisational measures designed to protect
        personal data against unauthorised or unlawful processing and against
        accidental loss, destruction or damage. These measures may include
        authentication controls, role-based access controls, encryption,
        logging, monitoring and security management processes.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">
        13. Cookies and Similar Technologies
      </h2>
      <p>
        The platform uses cookies and similar technologies for essential platform
        functions and, where applicable, other purposes as explained in the
        Cookie Notice.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">14. Complaints</h2>
      <p>
        If you have concerns about how personal data is handled, please contact
        us first at{" "}
        <a href="mailto:privacy@aq-trials.com">privacy@aq-trials.com</a>.
      </p>
      <p>
        You also have the right to complain to the Information
        Commissioner&rsquo;s Office.
      </p>
    </section>

    <section className="privacy-section privacy-section-last">
      <h2 className="privacy-section-title">15. Changes to This Notice</h2>
      <p>
        We may update this Privacy Notice from time to time to reflect changes
        in the platform, our practices, legal requirements or regulatory
        guidance. The latest version will be made available through the login
        page or platform.
      </p>
    </section>
  </>
);

const CookieNoticeTab = () => (
  <>
    <div className="privacy-content-meta">
      <span className="privacy-content-meta-label">Version:</span>
      <span className="privacy-content-meta-date">1.0</span>
      <span className="privacy-content-meta-label">Effective date:</span>
      <span className="privacy-content-meta-date">1st May 2025</span>
    </div>

    <section className="privacy-section">
      <h2 className="privacy-section-title">1. Introduction</h2>
      <p>
        This Cookie Notice explains how AscensionQ uses cookies and similar
        technologies in connection with the login page, platform access and
        related services.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">2. What Are Cookies?</h2>
      <p>
        Cookies are small text files placed on a user&rsquo;s device when
        visiting a website or online service. Similar technologies may also store
        or access information on a user&rsquo;s device for related purposes.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">3. Strictly Necessary Cookies</h2>
      <p>
        AscensionQ uses strictly necessary cookies and similar technologies to:
      </p>
      <ul>
        <li>enable secure sign-in and user authentication;</li>
        <li>maintain authenticated sessions;</li>
        <li>support essential security and fraud-prevention controls;</li>
        <li>
          manage load balancing and service availability where required; and
        </li>
        <li>
          remember technical settings needed for the platform to function.
        </li>
      </ul>
      <p>
        These cookies are necessary for the operation of the requested service
        and do not require consent under the applicable cookies rules, although
        users should still be given clear information about them.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">
        4. Analytics and Other Non-Essential Cookies
      </h2>
      <p>
        If AscensionQ uses analytics, performance, functionality or preference
        cookies that are not strictly necessary, these will only be used in
        accordance with applicable law and, where required, only after obtaining
        the user&rsquo;s consent.
      </p>
      <p>
        Where non-essential cookies are used, additional information will be
        provided about:
      </p>
      <ul>
        <li>the purpose of those cookies;</li>
        <li>the categories used;</li>
        <li>how long they remain on the device; and</li>
        <li>how users can accept, reject or change their preferences.</li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">5. Third-Party Cookies</h2>
      <p>
        AscensionQ does not use advertising cookies on the login page.
      </p>
      <p>
        If any third-party cookies are introduced in future, we will update this
        Cookie Notice and, where required, request consent before they are set.
      </p>
    </section>

    <section className="privacy-section">
      <h2 className="privacy-section-title">6. Managing Cookies</h2>
      <p>
        Most browsers allow users to control cookies through browser settings.
        However, disabling strictly necessary cookies may prevent secure login or
        parts of the platform from functioning correctly.
      </p>
    </section>

    <section className="privacy-section privacy-section-last">
      <h2 className="privacy-section-title">7. Further Information</h2>
      <p>
        For more information about how personal data is handled, please refer to
        the Privacy Notice.
      </p>
      <p>
        For questions about cookies or similar technologies used by AscensionQ,
        please contact:{" "}
        <a href="mailto:privacy@aq-trials.com">privacy@aq-trials.com</a>
      </p>
    </section>
  </>
);

const ContactTab = () => (
  <>
    <section className="privacy-section">
      <h2 className="privacy-section-title">Company Information</h2>
      <p>
        <strong>Ascension-Q Ltd</strong>
        <br />
        Registered in England and Wales
        <br />
        Company number: 10833152
        <br />
        Registered office: C/O 264 Manchester Road, Warrington, Cheshire, WA1
        3RB
      </p>
    </section>

    <section className="privacy-section privacy-section-last">
      <h2 className="privacy-section-title">Get in Touch</h2>
      <div className="legal-contact-grid">
        <div className="legal-contact-card">
          <h4>General Enquiries</h4>
          <a href="mailto:info@aq-trials.com">info@aq-trials.com</a>
        </div>
        <div className="legal-contact-card">
          <h4>Support</h4>
          <a href="mailto:support@aq-trials.com">support@aq-trials.com</a>
        </div>
        <div className="legal-contact-card">
          <h4>Legal</h4>
          <a href="mailto:legal@aq-trials.com">legal@aq-trials.com</a>
        </div>
        <div className="legal-contact-card">
          <h4>Privacy</h4>
          <a href="mailto:privacy@aq-trials.com">privacy@aq-trials.com</a>
        </div>
      </div>
    </section>
  </>
);

export default LegalPolicies;
